import { ITaskBlock } from '../../blocks-core/ITaskBlock'
import { Logger } from '../Logger'

/**
 * Creates a proxy object for a block that allows dynamic method calls.
 * This proxy is designed to work with Pyodide, preventing it from treating
 * the object as a Promise and allowing dynamic method access.
 *
 * Method execution errors are caught and logged to the console logger
 * to prevent Python script errors from crashing the app.
 *
 * @param block - The task block to create a proxy for
 * @param wrapFunction - Optional function wrapper to convert arguments (e.g., from Python to JS)
 * @returns A proxy object that forwards method calls to block.executeMethod
 */
export function createBlockProxy(
    block: ITaskBlock,
    wrapFunction?: (fn: (...args: unknown[]) => unknown) => (...args: unknown[]) => unknown
): Record<string, (...args: unknown[]) => unknown> {
    const methods = Object.create(null)

    return new Proxy(methods, {
        get: (_target, methodName: string | symbol) => {
            const methodStr = String(methodName)

            // exclude Promise methods to prevent Pyodide from wrapping in future object
            if (methodStr === 'then' || methodStr === 'catch' || methodStr === 'finally') {
                return undefined
            }

            const executeMethodCall = (...args: unknown[]) => {
                try {
                    return block.executeMethod(methodStr, ...args)
                } catch (error) {
                    const logger = Logger.getInstance()
                    logger.error(
                        `Block method "${methodStr}" failed on block "${block.name || block.id}"`,
                        {
                            source: 'BlockProxy',
                            error: error instanceof Error ? error : String(error),
                            context: {
                                blockId: block.id,
                                blockName: block.name,
                                method: methodStr,
                                hint: 'Check that the method exists and the arguments are correct.',
                            },
                        }
                    )
                    return undefined
                }
            }

            // Wrap with conversion function if provided
            return wrapFunction ? wrapFunction(executeMethodCall) : executeMethodCall
        },
        has: (_target, prop: string | symbol) => {
            const propStr = String(prop)
            // exclude Promise methods to prevent Pyodide from treating as Promise
            if (propStr === 'then' || propStr === 'catch' || propStr === 'finally') {
                return false
            }
            return true
        },
        ownKeys: () => {
            // return empty array to indicate this is a plain object with dynamic methods
            // so that it doesn't cache the methods
            return []
        },
    })
}
